import {TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { Messages } from '~src/codegen/Localize';
import { calculateUrlFromUrlName, calculateUrlNameFromSiteName, SiteIdValidRegExp } from '~src/settings/general-settings/components/sections/site-name-and-id/SiteNameAndIdBusinessLogic';


export type CreateSiteDialogBodyProps = {
}

export type CreateSiteDialogBodyState = {
  readonly siteName: string;
  readonly siteId: string;
  readonly siteUrl: string;
  readonly showInvalidSiteUrlErrorMessage: boolean;
  readonly showDuplicateSiteNameErrorMessage: boolean;
  readonly showDuplicateSiteUrlErrorMessage: boolean;
}

const textFieldBodyStyle: React.CSSProperties = {
  width: '100%',
  marginBottom: '16px'
};

const urlBodyStyle: React.CSSProperties = {
  width: '100%',
  marginBottom: '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

export function CreateSiteDialogBody(
  props: ReactActionDialogBodyProps<CreateSiteDialogBodyProps, CreateSiteDialogBodyState>
): JSX.Element {

  const onSiteNameChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const siteName = e.target.value.substring(0, 100);
      const siteUrlName = calculateUrlNameFromSiteName(siteName);
      const siteUrl = calculateUrlFromUrlName(siteUrlName);
      const showDuplicateSiteNameErrorMessage = props.dialogState.showDuplicateSiteNameErrorMessage ? (siteName === props.dialogState.siteName) : false;
      const showDuplicateSiteUrlErrorMessage = props.dialogState.showDuplicateSiteUrlErrorMessage ? (siteUrlName === props.dialogState.siteId) : false;
      props.updateDialogState({ siteName: siteName, siteId: siteUrlName, siteUrl: siteUrl, showInvalidSiteUrlErrorMessage: isSiteUrlNameInvalid(siteUrlName),
        showDuplicateSiteNameErrorMessage: showDuplicateSiteNameErrorMessage, showDuplicateSiteUrlErrorMessage: showDuplicateSiteUrlErrorMessage });
    },
    [props]
  );

  const onSiteIdChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const siteId = e.target.value.substring(0, 100);
      const siteUrl = calculateUrlFromUrlName(siteId);
      const showInvalidSiteUrlErrorMessage = isSiteUrlNameInvalid(siteId);
      const showDuplicateSiteUrlErrorMessage = props.dialogState.showDuplicateSiteUrlErrorMessage ? (siteId === props.dialogState.siteId) : false;
      props.updateDialogState({ siteId, siteUrl, showInvalidSiteUrlErrorMessage, showDuplicateSiteUrlErrorMessage });
    },
    [props]
  );

  const isSiteUrlNameInvalid = (siteUrlName: string) => {
    return siteUrlName ? !SiteIdValidRegExp.InvalidTest.test(siteUrlName) : false;
  };

  const getStyledUrlName = (urlName: string): JSX.Element => {
    const boldUrlNameTextIndex = urlName.lastIndexOf('/') + 1;
    const boldUrlNameText = urlName.substr(boldUrlNameTextIndex, urlName.length - boldUrlNameTextIndex);
    const normalUrlNameText = urlName.substr(0, boldUrlNameTextIndex);
    return (<span>{normalUrlNameText}<span style={{fontFamily: 'BentonSans-Medium'}}>{boldUrlNameText}</span></span>);
  };

  const isSiteUrlValid  = () => {
    return !props.dialogState.showInvalidSiteUrlErrorMessage && !props.dialogState.showDuplicateSiteUrlErrorMessage;
  };

  const isSiteNameValid  = () => {
    return !props.dialogState.showDuplicateSiteNameErrorMessage;
  };

  const getSiteUrlErrorMessage  = () => {
    if (props.dialogState.showInvalidSiteUrlErrorMessage) {
      return Messages.siteIDInvalid();
    } else if (props.dialogState.showDuplicateSiteUrlErrorMessage) {
      return Messages.createSiteDialog_duplicateSiteNameError();
    }
    return undefined;
  };

  const getSiteNameErrorMessage  = () => {
    if (props.dialogState.showDuplicateSiteNameErrorMessage) {
      return Messages.createSiteDialog_duplicateSiteIdError();
    }
    return undefined;
  };

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <>
      <div style={textFieldBodyStyle}>
        <TextFieldWidget
          className='tb-full-width'
          style={{ width: '100%' }}
          kind='line'
          label={Messages.createSiteDialog_siteNameFieldTitle()}
          placeholder={Messages.createSiteDialog_siteNameFieldPlaceholder()}
          maxLength={100}
          value={props.dialogState.siteName}
          onChange={onSiteNameChange}
          disabled={props.actionBusy}
          testId={'site-name'}
          valid={isSiteNameValid() ? undefined : false}
          message={getSiteNameErrorMessage()}
        />
      </div>
      <div style={textFieldBodyStyle}>
        <TextFieldWidget
          className='tb-full-width'
          style={{ width: '100%' }}
          kind='line'
          label={Messages.createSiteDialog_siteIdFieldTitle()}
          placeholder={Messages.createSiteDialog_siteIdFieldPlaceholder()}
          maxLength={100}
          value={props.dialogState.siteId}
          onChange={onSiteIdChange}
          disabled={props.actionBusy}
          testId={'site-id'}
          valid={isSiteUrlValid() ? undefined : false}
          message={getSiteUrlErrorMessage()}
        />
      </div>
      <div style={urlBodyStyle}>
          <span>{Messages.url_colon()}</span>
          <span title={props.dialogState.siteUrl} data-tb-test-id={'origin-url'}>&nbsp; {getStyledUrlName(props.dialogState.siteUrl)}</span>
      </div>
    </>
  );
}
